import { useCallback, useEffect, useMemo } from 'react';
import type { ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useLocation, useNavigate } from 'react-router';

import GA from '/business/shared/GA';
import { setEditingItemId } from '/business/shared/state/actions/form';
import { urlApplicationRoot, userPermissions } from '/business/shared/util/Statics';
import useUrlBuilder from '/business/shared/hooks/useUrlBuilder';
import lazyLoad from '/shared/components/functional/lazyLoad';
import layoutTypes from './layoutTypes';
import type { LayoutType } from './layoutTypes';
import DecoratedRoute from './useGetDecoratedRoute';
import type { AppDispatch, RootState } from '/business/shared/state/store';

import {
  getReductionPlanVersionSelector,
  hasAgreedToWelcomeSelector,
} from '/business/shared/state/selectors/application/basket_meta/getBasketMetaSelector';
import getApplicationSelector from '/business/shared/state/selectors/application/getApplicationSelector';
import getBasketStateAppIdSelector from '/business/shared/state/selectors/application/update_syncing/getBasketStateAppIdSelector';
import isConfirmedCompletedSelector from '/business/shared/state/selectors/application/workflow/isConfirmedCompletedSelector';
import getAuthRuleSelector from '/business/shared/state/selectors/auth/getAuthRuleSelector';
import hasMissingUserInfoSelector from '/business/shared/state/selectors/auth/hasMissingUserInfoSelector';
import isAuthedSelector from '/business/shared/state/selectors/auth/isAuthedSelector';
import getApplicationIdSelector from '/business/shared/state/selectors/application/getApplicationIdSelector';
import getApplicationCarbonCreditServicesEnabledSelector from '/business/shared/state/selectors/application/model/getApplicationCarbonCreditServicesEnabledSelector';
import isReducePageUnlockedSelector from '/business/shared/state/selectors/application/isReducePageUnlockedSelector';
import getSiblingApplicationsSelector from '/business/shared/state/selectors/application/model/getSiblingApplicationsSelector';
import setActiveApplication from '/business/shared/state/actions/applications/setActiveApplication';

import Loading from '/shared/components/Loading';
import PageError from '/shared/components/PageError';
import DevError from '/shared/errors/DevError';

type RouteComponentImport =
  | Promise<{ default: ComponentType<Record<string, never>> }>
  | (() => Promise<{ default: ComponentType<Record<string, never>> }>);

type RouteHandlerProps = {
  component?: RouteComponentImport | null;
  redirectTo?: string | null;
  renderLayout?: LayoutType;
  requiresCarbonCreditServices?: boolean;
  requiresReportingAuth?: boolean;
  requiresConfirmedCompleted?: boolean;
  requiresApplication?: boolean;
  requiresAgreement?: boolean;
  requiresReductionOnboarded?: boolean;
  requiresReductionNotOnboarded?: boolean;
  requiresAuth?: boolean;
  hasFooter?: boolean;
  hasFooterFixedToBottom?: boolean;
};

function RouteHandler({
  component = null,
  redirectTo = null,
  renderLayout = layoutTypes.EMPTY,
  requiresReportingAuth = false,
  requiresConfirmedCompleted = false,
  requiresApplication = false,
  requiresAgreement = false,
  requiresReductionOnboarded = false,
  requiresReductionNotOnboarded = false,
  requiresCarbonCreditServices = false,
  requiresAuth = false,
  hasFooter = false,
  hasFooterFixedToBottom = false,
}: RouteHandlerProps) {
  const dispatch = useDispatch<AppDispatch>();

  const stateIsAuthed = useSelector(isAuthedSelector) || false;
  const currentApplication = useSelector(getApplicationSelector);
  const legacyWelcomeAgreed = useSelector(hasAgreedToWelcomeSelector);
  const stateIsAgreed = currentApplication?.welcomeAgreed || legacyWelcomeAgreed;
  const stateIsReductionOnboarded = useSelector(isReducePageUnlockedSelector);
  const stateIsConfirmedCompleted = useSelector(isConfirmedCompletedSelector) || false;
  const stateBasketReplaced = useSelector((state: RootState) => state.basket?.replaced) || false;
  const stateBasketAppId = useSelector(getBasketStateAppIdSelector);
  const stateApplicationId = useSelector(getApplicationIdSelector) || false;
  const carbonCreditServicesEnabled = useSelector(getApplicationCarbonCreditServicesEnabledSelector);
  const stateApplicationIsFetching = useSelector((state: RootState) => state.applications?.fetching) || false;
  const stateIsReportingDashboardAuth =
    useSelector((state: RootState) => getAuthRuleSelector(state, userPermissions.VIEW_REPORTING_DASHBOARD)) || false;
  const hasMissingUserInfo = useSelector(hasMissingUserInfoSelector);
  const siblingApplications = useSelector(getSiblingApplicationsSelector);
  const hasMultipleReportingPeriods = siblingApplications && siblingApplications.length > 1;
  const stateReductionPlanVersion = useSelector(getReductionPlanVersionSelector);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const relativeUrl = `${pathname}${search}`;
  const { getApplicationUrl, getUrlWithAppId } = useUrlBuilder();

  const LoadedRoute = useMemo(() => (component ? lazyLoad(component, 400, true) : null), [component]);

  useEffect(() => {
    dispatch(setEditingItemId(null, ''));
    GA.triggerPageview({
      page: pathname,
    });
  }, [dispatch, pathname]);

  if (hasFooter && hasFooterFixedToBottom) {
    throw new DevError("Cannot have both 'hasFooter' and 'hasFooterFixedToBottom'");
  }

  const urlBasedAppId = useMemo(() => new URLSearchParams(search).get('appId'), [search]);
  const hasUrlBasedAppId = urlBasedAppId !== null && urlBasedAppId !== 'null';
  const parsedUrlBasedAppId = hasUrlBasedAppId ? parseInt(urlBasedAppId, 10) : null;
  const handleActiveApplication = useCallback(() => {
    const params = new URLSearchParams(search);
    const urlBasedAppIdLocal = params.get('appId');
    if (urlBasedAppIdLocal !== null && urlBasedAppIdLocal !== 'null') {
      if (parseInt(urlBasedAppIdLocal, 10) !== stateApplicationId) {
        dispatch(setActiveApplication(urlBasedAppIdLocal));
      }
    }
    if (
      !urlBasedAppIdLocal &&
      (pathname.startsWith(`/${urlApplicationRoot}`) || pathname.startsWith(`/${urlApplicationRoot}-settings`))
    ) {
      params.set('appId', String(stateApplicationId));
      navigate(`${pathname}?${params}`, { replace: true });
      return;
    }
    if (!urlBasedAppIdLocal && stateApplicationId) {
      dispatch(setActiveApplication(null));
    }
  }, [dispatch, navigate, search, stateApplicationId, pathname]);

  useEffect(() => {
    handleActiveApplication();
  }, [handleActiveApplication]);

  const renderFunction = useCallback(() => {
    if (requiresAuth && !stateIsAuthed) {
      return (
        <Navigate
          replace
          to={getUrlWithAppId(`/sso/welcome?${new URLSearchParams({ redirect: relativeUrl })}`, urlBasedAppId)}
        />
      );
    }
    if (requiresReductionNotOnboarded && stateIsReductionOnboarded) {
      const defaultReducePage = hasMultipleReportingPeriods ? 'comparison' : 'hotspots';
      return <Navigate replace to={getApplicationUrl(`/reduce/${defaultReducePage}`, urlBasedAppId)} />;
    }
    if (requiresReductionOnboarded && !stateIsReductionOnboarded && stateReductionPlanVersion !== null) {
      return <Navigate replace to={getApplicationUrl('/reduce/onboarding', urlBasedAppId)} />;
    }

    const allowedRoutesWithMissingUserInfo = ['/sso/user-info', '/sso/logout', '/privacy-policy', '/invite'];
    if (
      hasMissingUserInfo &&
      !allowedRoutesWithMissingUserInfo.some((allowedRoute) => pathname.startsWith(allowedRoute))
    ) {
      return <Navigate replace to="/sso/user-info" />;
    }

    if (
      requiresApplication &&
      hasUrlBasedAppId &&
      (parsedUrlBasedAppId !== stateApplicationId || (stateApplicationIsFetching && !currentApplication))
    ) {
      return <Loading isWrapped />;
    }

    if (
      requiresApplication &&
      ((!hasUrlBasedAppId && !stateApplicationId) ||
        (hasUrlBasedAppId &&
          parsedUrlBasedAppId === stateApplicationId &&
          !currentApplication &&
          !stateApplicationIsFetching))
    ) {
      return <PageError error={t('business:Errors.appMeta.notFound', 'This application could not be found')} />;
    }

    if (requiresAgreement && !stateIsAgreed) {
      const parsedBasketAppId =
        stateBasketAppId === null || stateBasketAppId === undefined ? null : parseInt(String(stateBasketAppId), 10);
      // The basket holds stateAppId 0 when the form state load failed
      const hasFinishedLoadingFormState =
        stateBasketReplaced && (parsedBasketAppId === stateApplicationId || parsedBasketAppId === 0);
      if (hasFinishedLoadingFormState) {
        return <Navigate replace to={getApplicationUrl('', urlBasedAppId)} />;
      }
      return (
        <Loading
          isWrapped
          fallback={{
            timeoutMS: 10000,
            component: <PageError error={t('shared:unauthorisedRequest', 'Unauthorised request')} />,
          }}
        />
      );
    }
    if (requiresReportingAuth && !stateIsReportingDashboardAuth) {
      // @i18n: You do not have permission to view the reporting dashboard
      return <PageError error={t('business:Errors.routeGuards.reportingAuth')} />;
    }
    if (requiresConfirmedCompleted && !stateIsConfirmedCompleted && !stateApplicationIsFetching) {
      // @i18n: This carbon review must be complete before you can view this page
      return <PageError error={t('business:Errors.routeGuards.confirmedCompleted')} />;
    }
    if (requiresCarbonCreditServices && !carbonCreditServicesEnabled && !stateApplicationIsFetching) {
      // @i18n: Carbon credit services are not available for this carbon review
      return <PageError error={t('business:Errors.routeGuards.carbonCreditServices')} />;
    }
    if (redirectTo) {
      return <Navigate replace to={`${redirectTo}${search}`} />;
    }
    return false;
  }, [
    currentApplication,
    hasUrlBasedAppId,
    parsedUrlBasedAppId,
    stateApplicationIsFetching,
    stateIsReportingDashboardAuth,
    stateIsAgreed,
    stateIsAuthed,
    stateIsConfirmedCompleted,
    stateIsReductionOnboarded,
    stateReductionPlanVersion,
    hasMissingUserInfo,
    stateBasketReplaced,
    stateBasketAppId,
    requiresAgreement,
    requiresApplication,
    requiresAuth,
    requiresReportingAuth,
    requiresConfirmedCompleted,
    requiresCarbonCreditServices,
    requiresReductionOnboarded,
    requiresReductionNotOnboarded,
    hasMultipleReportingPeriods,
    carbonCreditServicesEnabled,
    stateApplicationId,
    t,
    relativeUrl,
    urlBasedAppId,
    getApplicationUrl,
    getUrlWithAppId,
    pathname,
    search,
    redirectTo,
  ]);

  const render = renderFunction();
  if (render) {
    // If we have a renderedFunction, then process that, rather than just showing the default Route
    return <>{render}</>;
  }

  return (
    <DecoratedRoute
      LoadedRoute={LoadedRoute}
      layout={renderLayout}
      hasFooter={hasFooter}
      hasFooterFixedToBottom={hasFooterFixedToBottom}
    />
  );
}

export default RouteHandler;
